import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpErrorResponse } from '@angular/common/http';
import { BehaviorSubject, Observable, of, throwError } from 'rxjs';
import { map, catchError, tap } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { AuthService } from './auth.service';
import { TenantService } from './tenant.service';
import { Capacitor } from '@capacitor/core';

@Injectable({ providedIn: 'root' })
export class UserService {

  private marketInfoSubject = new BehaviorSubject<any>(null);
  marketInfo$ = this.marketInfoSubject.asObservable();
  // Which market the cached marketInfo belongs to
  private cachedMarketId: string | null = null;

  private vendorInfoSubject = new BehaviorSubject<any>(null);
  vendorInfo$ = this.vendorInfoSubject.asObservable();
  // Which market:vendor the cached vendorInfo belongs to
  private cachedVendorKey: string | null = null;

  // Vendor chosen in the dashboard dropdown (shared-email accounts).
  // The top bar (welcome name + badge) and the notification screen follow this.
  private selectedVendorSubject = new BehaviorSubject<{ VendorId: number; VendorName: string } | null>(null);
  selectedVendor$ = this.selectedVendorSubject.asObservable();

  constructor(
    private http: HttpClient,
    private authService: AuthService,
    private tenantService: TenantService
  ) {
    // Restore the choice after an app restart / page reload.
    this.selectedVendorSubject.next(this.readStoredSelectedVendor());
  }

  public options = { headers: new HttpHeaders({ 'Content-Type': 'application/json' }) };

  /** Returns the API base URL.
   *  On native: always reads from TenantService (supports runtime config changes).
   *  On web: reads from environment (set from config.json at startup). */
  get baseURL(): string {
    if (Capacitor.isNativePlatform()) {
      return this.tenantService.getConfig()?.apiBaseUrl ?? environment.apiBaseUrl;
    }
    return environment.apiBaseUrl;
  }

  generateUUID(): string {
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
      const r = Math.random() * 16 | 0;
      const v = c === 'x' ? r : (r & 0x3 | 0x8);
      return v.toString(16);
    });
  }

  // ---------------- selected vendor ----------------

  setSelectedVendor(vendor: { VendorId: number; VendorName: string } | null): void {
    this.selectedVendorSubject.next(vendor);
    this.storeSelectedVendor(vendor);
  }

  getSelectedVendorSnapshot(): { VendorId: number; VendorName: string } | null {
    return this.selectedVendorSubject.value;
  }

  clearSelectedVendor(): void {
    this.selectedVendorSubject.next(null);
    this.storeSelectedVendor(null);
  }

  /**
   * The vendor every vendor-specific call should use:
   * the one chosen in the dropdown (e.g. 374), otherwise the logged-in default (e.g. 407).
   */
  getActiveVendorId(): number {
    const selected = this.selectedVendorSubject.value?.VendorId;
    return selected ? Number(selected) : Number(this.getVendorId());
  }

  // On native, sessionStorage's tabId is regenerated on every launch,
  // so a tabId-based key would never be found again after a restart.
  private get selectedVendorKey(): string {
    if (Capacitor.isNativePlatform()) {
      return 'selectedVendor-native';
    }
    if (!sessionStorage.getItem('tabId')) {
      sessionStorage.setItem('tabId', this.generateUUID());
    }
    return `selectedVendor-${sessionStorage.getItem('tabId')}`;
  }

  private storeSelectedVendor(vendor: { VendorId: number; VendorName: string } | null): void {
    try {
      if (vendor) {
        localStorage.setItem(this.selectedVendorKey, JSON.stringify(vendor));
      } else {
        localStorage.removeItem(this.selectedVendorKey);
      }
    } catch { /* storage unavailable: the in-memory value still works */ }
  }

  private readStoredSelectedVendor(): { VendorId: number; VendorName: string } | null {
    try {
      const raw = localStorage.getItem(this.selectedVendorKey);
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  }

  // ---------------- session ----------------

  setSessionData(marketId: string, vendorId: string): void {
    // A fresh login must never inherit a previous vendor choice.
    this.clearSelectedVendor();

    if (Capacitor.isNativePlatform()) {
      // Persist alongside the tenant config so it survives an app restart —
      // sessionStorage-keyed tabId (below) doesn't survive on native.
      const cfg = this.tenantService.getConfig();
      if (cfg) {
        this.tenantService.saveConfig({ ...cfg, vendorId });
      }
    }
    if (!sessionStorage.getItem('tabId')) {
      sessionStorage.setItem('tabId', this.generateUUID());
    }
    const tabId = sessionStorage.getItem('tabId');
    localStorage.setItem(`marketId-${tabId}`, marketId);
    localStorage.setItem(`vendorId-${tabId}`, vendorId);
  }

  /** Returns the current marketId.
   *  On native: reads from TenantService (survives app restarts).
   *  On web: reads from localStorage keyed by tabId (legacy behaviour). */
  getMarketId(): string | null {
    if (Capacitor.isNativePlatform()) {
      return this.tenantService.getConfig()?.marketId ?? null;
    }
    const tabId = sessionStorage.getItem('tabId');
    return localStorage.getItem(`marketId-${tabId}`);
  }

  /** Returns the logged-in (default) vendorId.
   *  For the vendor chosen in the dropdown use getActiveVendorId(). */
  getVendorId(): string | null {
    if (Capacitor.isNativePlatform()) {
      return this.tenantService.getConfig()?.vendorId ?? null;
    }
    const tabId = sessionStorage.getItem('tabId');
    return localStorage.getItem(`vendorId-${tabId}`);
  }

  /** Drops cached market/vendor info so a market switch or logout never shows stale data.
   *  Also drops the dropdown vendor choice, since it belongs to one market. */
  clearMarketCache(): void {
    this.cachedMarketId = null;
    this.cachedVendorKey = null;
    this.marketInfoSubject.next(null);
    this.vendorInfoSubject.next(null);
    this.clearSelectedVendor();
  }

  clearSession(): void {
    if (!sessionStorage.getItem('tabId')) {
      sessionStorage.setItem('tabId', this.generateUUID());
    }
    const tabId = sessionStorage.getItem('tabId');
    localStorage.removeItem(`marketId-${tabId}`);
    localStorage.removeItem(`vendorId-${tabId}`);
    this.clearMarketCache();
  }

  getToken(userData: any): Observable<any> {
    const url = `${this.baseURL}/user`;
    return this.http.post<any>(url, userData, this.options).pipe(
      map(res => {
        if (res.Token) {
          this.authService.isLoggedIn = true;
          localStorage.setItem('token', res.Token);
          return res;
        } else {
          this.authService.isLoggedIn = false;
        }
      }),
      catchError(this.handleError)
    );
  }

  clearToken(): void {
    localStorage.removeItem('token');
    this.authService.isLoggedIn = false;
    this.clearSelectedVendor();   // logout: the next user must not inherit the choice

    // Clear only the active profile's own token so logging out of one
    // market/server doesn't affect sessions on other saved profiles.
    if (Capacitor.isNativePlatform()) {
      const active = this.tenantService.getActiveProfile();
      if (active) {
        this.tenantService.upsertProfile({ ...active, token: undefined });
      }
    }
  }

  forgotPassword(marketId: any, email: string): Observable<any> {
    const url = `${this.baseURL}/user/VendorWILoginForgotPassword?marketId=${marketId}`;
    return this.http.post<any>(url, { email }, this.options).pipe(
      map(res => res as any[]),
      catchError(this.handleError)
    );
  }

  resetVendorWILoginPassword(marketId: any, vendorResetPassModel: any): Observable<any> {
    const url = `${this.baseURL}/user/ResetVendorWILoginPassword?marketId=${marketId}`;
    return this.http.post<any>(url, vendorResetPassModel, this.options).pipe(
      map(res => res as any[]),
      catchError(this.handleError)
    );
  }

  getVendorById(vendorId: number, marketId: number): Observable<any> {
    const key = `${marketId}:${vendorId}`;
    if (this.vendorInfoSubject.value && this.cachedVendorKey === key) {
      return of(this.vendorInfoSubject.value);
    }
    return this.http.get<any>(
      `${this.baseURL}/General/GetVendorInfoById?vendorId=${vendorId}&marketId=${marketId}`,
      { observe: 'response' }
    ).pipe(
      map(res => res.body),
      tap(result => {
        this.cachedVendorKey = key;
        this.vendorInfoSubject.next(result);
      }),
      catchError(this.handleError)
    );
  }

  getMarketName(marketId: number): Observable<any> {
    const key = String(marketId);
    if (this.marketInfoSubject.value && this.cachedMarketId === key) {
      return of(this.marketInfoSubject.value);
    }
    return this.http.get<any>(
      `${this.baseURL}/General/GetMarketName?marketId=${marketId}`,
      { observe: 'response' }
    ).pipe(
      map(res => res.body),
      tap(result => {
        this.cachedMarketId = key;
        this.marketInfoSubject.next(result);
      }),
      catchError(this.handleError)
    );
  }

  private handleError(error: HttpErrorResponse): Observable<never> {
    console.error('server error:', error);
    if (error.error instanceof Error) {
      return throwError(() => error.error.message);
    }
    return throwError(() => error || 'Server error');
  }
}